import { feature, featureCollection } from "@turf/turf";
import { FeatureCollection } from "geojson";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { GqlPlannerV2MapElementPointType } from "~/__generated__";
import GaiaMapDataLayer from "~/modules/map/components/GaiaMapDataLayer";
import { GaiaMapFeatureHoverListener } from "~/modules/map/components/GaiaMapEventListeners/GaiaMapFeatureHoverListener";
import { GaiaMapStyleCircle } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleCircle";

import { ROUTE_LINE_COLOR_BY_TYPE } from "../RoutesLayerV2";
import { FieldworkerHomeMarkersLayerFragment } from "./fragments.generated";

interface PassengerHomeMarkerProperty {
    _id: string;
    type: GqlPlannerV2MapElementPointType;
    label: string;
}

const PASSENGER_HOME_SOURCE_ID = "passenger_home_address_markers_source";
const PASSENGER_HOME_LAYER_ID = "passenger_home_address_markers";

interface FieldworkerHomeMarkersLayerProps {
    points: FieldworkerHomeMarkersLayerFragment[];
}

function usePassengerHomeMarkersGeoJson(points: FieldworkerHomeMarkersLayerFragment[]): FeatureCollection {
    const { t } = useTranslation("plannerV2");

    return useMemo(() => {
        const features = points.map(point => {
            const names = point.plannerShiftTravels
                ?.map(travel => travel.plannerShift?.fieldworker.user.firstName)
                .filter(Boolean)
                .join(", ");
            const properties: PassengerHomeMarkerProperty = {
                _id: point._id,
                type: point.type,
                label: t("passengerHomeAddressMarkers.home", { names }),
            };
            return feature(point.locationPoint, properties);
        });
        return featureCollection(features);
    }, [points, t]);
}

export function FieldworkerHomeMarkersLayer({ points }: FieldworkerHomeMarkersLayerProps) {
    const [isHoveringOverHomeAddresses, setIsHoveringOverHomeAddresses] = useState(false);
    const data = usePassengerHomeMarkersGeoJson(points);

    return (
        <>
            <GaiaMapDataLayer id={PASSENGER_HOME_SOURCE_ID} data={data} />

            <GaiaMapStyleCircle
                id={PASSENGER_HOME_LAYER_ID}
                source={PASSENGER_HOME_SOURCE_ID}
                radius={10}
                color={ROUTE_LINE_COLOR_BY_TYPE.secondary}
                label={{
                    textField: ["get", "label"],
                    color: "homeAddress",
                    paint: {
                        "icon-opacity": isHoveringOverHomeAddresses ? 1 : 0,
                        "text-opacity": isHoveringOverHomeAddresses ? 1 : 0,
                    },
                }}
                filter={["!=", ["get", "_id"], isHoveringOverHomeAddresses ?? ""]}
            />

            <GaiaMapFeatureHoverListener
                layerIds={[PASSENGER_HOME_LAYER_ID]}
                onHoverCallback={() => {
                    setIsHoveringOverHomeAddresses(true);
                }}
                onHoverEndCallback={() => {
                    setIsHoveringOverHomeAddresses(false);
                }}
            />
        </>
    );
}
